import { useCallback } from "react";
import { useLocalStorage } from "../../shared/hooks/useLocalStorage";
import { useSession } from "./useSession";

/**
 * Nudges whose dismissal is remembered locally.
 *
 * v1 stored these as flags on the user's `activity.events`
 * (`taxWithholdingsAlertsDismissed`, `dashboardTaxWithholdingsPromotionDismissed`).
 * That resource is retired and v3 has no replacement — there is no
 * user-preferences resource, and `AcknowledgementName` is a legal-consent enum
 * with no member for a UI dismissal — so the flag lives in localStorage.
 *
 * Trade-off vs. v1: a dismissal no longer follows the person across devices or
 * browsers. It does survive sign-out (sign-out clears only named keys), which is
 * why the key is scoped to the account: another account signing in on the same
 * browser starts with its own nudges showing.
 */
export type DismissibleNudge =
  | "taxWithholdingsAlerts"
  | "dashboardTaxWithholdingsPromotion";

/**
 * Returns whether `nudge` has been dismissed for the signed-in account, and a
 * callback to dismiss it. Writes are picked up by every other instance of the
 * hook in the tab, so dismissing in one place hides the nudge everywhere it
 * renders without a refetch.
 */
export const useNudgeDismissal = (
  nudge: DismissibleNudge
): [boolean, () => void] => {
  const { accountId } = useSession();
  const [dismissed, setDismissed] = useLocalStorage(
    `nudge-dismissed:${nudge}:${accountId}`,
    false
  );

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, [setDismissed]);

  // Before the session resolves there is no account to scope the flag to, so
  // treat the nudge as not dismissed rather than reading a shared key.
  return [!!accountId && dismissed, dismiss];
};
